import type { EditorContext } from "../tools/EditorContext";
import { downloadSvg } from "../exporters/svgExporter";
import { downloadDxf } from "../exporters/dxfExporter";
import { downloadRaster } from "../exporters/rasterExporter";
import { downloadPdf } from "../exporters/pdfExporter";
import { isFeatureEnabled } from "../featureFlags";
import { markPaidFeature } from "./paidPreview";
import { AdvancedPrintDialog } from "./AdvancedPrintDialog";
import { VectorExportDialog } from "./VectorExportDialog";

export class ExportDialog {
  private host: HTMLElement;
  private ctx: EditorContext;
  private advanced: AdvancedPrintDialog;
  private vector: VectorExportDialog;
  private dialogEl: HTMLDialogElement | null = null;

  constructor(host: HTMLElement, ctx: EditorContext) {
    this.host = host;
    this.ctx = ctx;
    this.advanced = new AdvancedPrintDialog(host, ctx);
    this.vector = new VectorExportDialog(host, ctx);
  }

  open() {
    this.teardown();

    // SVG / DXF are always offered (#572): the basic path downloads
    // one-click with default settings. With the "Optimized vector export"
    // flag on, picking either opens the settings dialog instead (content /
    // operations / laser colors / Cricut / calibration boxes) — the old
    // separate "SVG (Cricut)" button became that dialog's Cricut checkbox.
    const vector = `
          <button class="btn" data-act="svg">SVG</button>`;
    const vectorDxf = `
          <button class="btn" data-act="dxf">DXF</button>`;

    // A native <dialog> opened with showModal() puts the rest of the page in the
    // inert top-layer background (focus stays trapped) and gives Esc-to-close for
    // free — mirroring the advanced print dialog. The header carries a ✕ button.
    const modal = document.createElement("dialog");
    modal.className = "modal export-modal";
    modal.innerHTML = `
      <header style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <span>Export pattern</span>
        <button class="btn" type="button" data-act="close" aria-label="Close" title="Close" style="border:none;background:transparent;font-size:18px;line-height:1;padding:2px 8px;cursor:pointer;color:inherit;">✕</button>
      </header>
      <div class="body">
        <div style="display:grid;gap:8px;">${vector}
          <button class="btn" data-act="pdf">PDF (multi-page + step-by-step instructions)</button>
          <button class="btn" data-act="png">PNG (600 DPI)</button>
          <button class="btn" data-act="jpeg">JPEG (600 DPI)</button>${vectorDxf}
        </div>
      </div>
      <footer>
        <button class="btn" type="button" data-act="close">Close</button>
      </footer>
    `;
    // PDF and SVG / DXF serve both a free basic export and a paid advanced
    // one (#1120): each wears the Preview badge only while its flag routes it
    // to the paid version.
    const markAct = (act: string, slug: string, flagOn: boolean) => {
      const el = modal.querySelector<HTMLElement>(`[data-act="${act}"]`);
      if (el) markPaidFeature(el, slug, flagOn);
    };
    markAct("pdf", "advanced-printing", isFeatureEnabled("advanced-printing"));
    markAct(
      "svg",
      "optimized-vector-export",
      isFeatureEnabled("vector-export"),
    );
    markAct(
      "dxf",
      "optimized-vector-export",
      isFeatureEnabled("vector-export"),
    );
    modal.addEventListener("click", async (ev) => {
      // A click landing on the dialog element itself is a click on the
      // ::backdrop (outside the content) — dismiss, like the old overlay.
      if (ev.target === modal) {
        this.close();
        return;
      }
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      const act = btn.dataset.act;
      if (act === "close") {
        this.close();
        return;
      }
      try {
        const project = this.ctx.history.state;
        const lib = this.ctx.library;
        switch (act) {
          case "svg":
            // Optimized vector export (#572): collect content / operation /
            // laser settings in the dialog first; basic mode is one-click.
            if (isFeatureEnabled("vector-export")) {
              this.close();
              this.vector.open("svg");
              return;
            }
            downloadSvg(project, lib);
            break;
          case "pdf":
            // With advanced printing on, collect orientation / paper / marks
            // in the settings dialog first (issue #289); otherwise export the
            // basic print-ready PDF straight away.
            if (isFeatureEnabled("advanced-printing")) {
              this.close();
              this.advanced.open("export");
              return;
            }
            await downloadPdf(project, lib);
            break;
          case "png":
            await downloadRaster(project, lib, { format: "png" });
            break;
          case "jpeg":
            await downloadRaster(project, lib, { format: "jpeg" });
            break;
          case "dxf":
            if (isFeatureEnabled("vector-export")) {
              this.close();
              this.vector.open("dxf");
              return;
            }
            downloadDxf(project, lib);
            break;
        }
        this.close();
      } catch (e) {
        alert("Export failed: " + (e as Error).message);
      }
    });

    this.host.appendChild(modal);
    this.dialogEl = modal;
    // Native Esc / programmatic close fires "close"; tear down then.
    modal.addEventListener("close", () => this.teardown());
    modal.showModal();
  }

  close() {
    // close() fires the "close" event, where teardown() removes the element.
    this.dialogEl?.close();
  }

  /** Remove the dialog element and reset state. Idempotent. */
  private teardown() {
    if (this.dialogEl) {
      this.dialogEl.remove();
      this.dialogEl = null;
    }
  }
}
